import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Menu,
  ChevronLeft,
  ChevronRight,
  User,
  Sun,
  Moon,
  LogOut,
  Settings,
  X,
  Globe,
  Check,
} from "lucide-react";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import TaskRemindersPopover from "../components/TaskRemindersPopover";
import BillingBanner from "../components/BillingBanner";
import SubscriptionBlocked from "../components/SubscriptionBlocked";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { cn } from "../lib/utils";

const DRAWER_WIDTH = 240;
const DRAWER_MINI = 64;

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { handleLogout, loading, suspended } = useContext(AuthContext);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const menuRef = useRef(null);
  const langMenuRef = useRef(null);

  const languages = [
    { code: "es", label: "Español" },
    { code: "en", label: "English" },
    { code: "pt", label: "Português" },
  ];
  // i18n.language puede venir como "es", "en-US", etc.; comparamos por prefijo.
  const currentLang = (i18n.language || "en").slice(0, 2);

  const handleChangeLanguage = (code) => {
    setLangMenuOpen(false);
    if (code === currentLang) return;
    // El detector persiste el idioma en localStorage (i18nextLng). Como la app
    // usa i18n.t(...) directo (sin el hook reactivo), recargamos para aplicarlo
    // en toda la interfaz.
    i18n.changeLanguage(code);
    window.location.reload();
  };

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setDrawerOpen(!mobile);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    setUserMenuOpen(false);
  };

  const handleClickLogout = () => {
    setUserMenuOpen(false);
    handleLogout();
  };

  const drawerClose = () => {
    if (isMobile) setDrawerOpen(false);
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "U";

  if (loading) return <BackdropLoading />;

  // The API answered 402: the installation is suspended for non-payment. The
  // takeover only talks to /billing/*, which enforcement leaves reachable.
  if (suspended) return <SubscriptionBlocked onLogout={handleLogout} />;

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Mobile overlay */}
      {isMobile && drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col bg-slate-900 dark:bg-slate-950 border-r border-slate-800 transition-all duration-200 ease-in-out flex-shrink-0 z-50",
          isMobile ? "fixed inset-y-0 left-0" : "relative",
          drawerOpen ? `w-[${DRAWER_WIDTH}px]` : `w-[${DRAWER_MINI}px]`,
          isMobile && !drawerOpen ? "-translate-x-full" : "translate-x-0"
        )}
        style={{ width: drawerOpen ? DRAWER_WIDTH : DRAWER_MINI }}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-3 py-4 min-h-[60px] border-b border-slate-800">
          {drawerOpen ? (
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-indigo-600 bg-clip-text text-transparent tracking-tight ml-1">
              Voxia
            </span>
          ) : (
            <span className="w-8 h-8 flex items-center justify-center bg-indigo-600 rounded-lg text-white font-bold text-sm mx-auto">
              V
            </span>
          )}
          <button
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors ml-auto"
            aria-label="Toggle sidebar"
          >
            {drawerOpen ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Nav items */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
        </div>

        {/* Sidebar footer */}
        <div className="border-t border-slate-800 p-3">
          {drawerOpen ? (
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.profile}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                {initials}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between px-4 h-[60px] bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex-shrink-0 z-30">
          <div className="flex items-center gap-3">
            {isMobile && (
              <button
                onClick={() => setDrawerOpen(true)}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen((v) => !v)}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1"
                aria-label="Language"
                title={i18n.t("mainDrawer.appBar.language")}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:block text-xs font-semibold uppercase">
                  {currentLang}
                </span>
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-40 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg py-1 z-50 animate-slide-up">
                  {languages.map((lng) => (
                    <button
                      key={lng.code}
                      onClick={() => handleChangeLanguage(lng.code)}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      {lng.label}
                      {currentLang === lng.code && (
                        <Check className="w-4 h-4 text-indigo-500" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Task reminders */}
            {user?.id && <TaskRemindersPopover />}

            {/* Notifications */}
            {user?.id && <NotificationsPopOver />}

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                  {initials}
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[120px] truncate">
                  {user?.name || "Usuario"}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-52 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg py-1 z-50 animate-slide-up">
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleOpenUserModal}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    {i18n.t("mainDrawer.appBar.user.profile")}
                  </button>
                  <button
                    onClick={handleClickLogout}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <BillingBanner />

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
          {children}
        </main>
      </div>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
    </div>
  );
};

export default LoggedInLayout;
